import { formatCurrency } from '@/lib/formatters';
import type { DiretoriaKpiTone } from '@/lib/diretoriaKpiTone';
import { formatVariacaoPct } from '@/lib/diretoriaPeriod';

export type DiretoriaExecutiveSource = 'api' | 'erp';

export interface DiretoriaExecutiveCard {
  id: string;
  label: string;
  value: string;
  sublabel?: string;
  trend?: string;
  trendPositive?: boolean;
  valueTone?: DiretoriaKpiTone;
  source: DiretoriaExecutiveSource;
  isSnapshot?: boolean;
  /** Badge sob o valor (Snapshot ou Posição no fim do período). */
  positionBadge?: string;
}

export interface DiretoriaDashboardStats {
  totalTitulos?: number;
  titulosAbertos?: number;
  titulosVencidos?: number;
  clientesInadimplentes?: number;
  valorTotal?: number;
  operadoresAtivos?: number;
  empresasAtivas?: number;
  aging1a2Dias?: { count?: number; valor?: number };
  aging3a6Dias?: { count?: number; valor?: number };
  agingAcima7Dias?: { count?: number; valor?: number };
  titulosPorEmpresa?: Array<{ empresa: string; count: number; valor: number }>;
  meta?: {
    periodApplied?: boolean;
    dataTo?: string | null;
    dataFrom?: string | null;
    dataReferencia?: string;
  };
}

export interface DiretoriaFaturamentoMesStats {
  totalPago: number;
  ano?: number;
  mes?: number;
  periodo?: { dataFrom: string; dataTo: string };
  anteriorTotal?: number;
  variacaoPct?: number | null;
  companyId?: string;
  empresaid?: number;
  querySql?: string;
}

export interface DiretoriaReceitaProjetadaStats {
  totalAReceber: number;
  competencias: Array<{ ano: number; mes: number; competencia: string; totalAReceber: number }>;
  periodo?: { dataFrom: string; dataTo: string };
  anteriorTotal?: number;
  variacaoPct?: number | null;
  companyId?: string;
  empresaid?: number;
  querySql?: string;
}

function formatMonthYear(mes?: number, ano?: number): string {
  if (!mes || !ano) return 'ERP FlyStart';
  return `${String(mes).padStart(2, '0')}/${ano}`;
}

function buildTrend(
  variacaoPct: number | null | undefined,
  compareLabel?: string
): Pick<DiretoriaExecutiveCard, 'trend' | 'trendPositive'> {
  if (variacaoPct == null || !Number.isFinite(variacaoPct)) return {};
  const vs = compareLabel ? `vs ${compareLabel}` : 'vs período comparativo';
  return {
    trend: `${formatVariacaoPct(variacaoPct)} ${vs}`,
    trendPositive: variacaoPct >= 0,
  };
}

function formatDataToPt(dataTo?: string | null): string | null {
  if (!dataTo) return null;
  const m = dataTo.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return dataTo;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

const COBRANCA_SNAPSHOT_SUB = 'Situação atual · não filtrado por período';

function cobrancaPositionSublabel(
  dashboard: DiretoriaDashboardStats | null | undefined,
  periodLabel?: string
): { sublabel: string; isSnapshot: boolean; badgeLabel?: string } {
  if (dashboard == null) return { sublabel: 'Cobrança', isSnapshot: false };
  const periodApplied = Boolean(dashboard.meta?.periodApplied);
  const dataToPt = formatDataToPt(dashboard.meta?.dataTo);
  if (periodApplied && (dataToPt || periodLabel)) {
    const pos = dataToPt ? `Posição em ${dataToPt}` : 'Posição no fim do período';
    return {
      sublabel: periodLabel ? `${pos} · ${periodLabel}` : pos,
      isSnapshot: false,
      badgeLabel: 'Posição no fim do período',
    };
  }
  return {
    sublabel: COBRANCA_SNAPSHOT_SUB,
    isSnapshot: true,
    badgeLabel: 'Snapshot',
  };
}

export function buildExecutiveCards(
  dashboard: DiretoriaDashboardStats | null | undefined,
  faturamento: DiretoriaFaturamentoMesStats | null | undefined,
  receitaProjetada: DiretoriaReceitaProjetadaStats | null | undefined,
  periodLabel?: string,
  compareLabel?: string
): DiretoriaExecutiveCard[] {
  const valorCarteira = Number(dashboard?.valorTotal ?? 0);
  const titulosVencidos = Number(dashboard?.titulosVencidos ?? 0);
  const clientesInad = Number(dashboard?.clientesInadimplentes ?? 0);
  const agingLeve = Number(dashboard?.aging1a2Dias?.count ?? 0);
  const competencias = receitaProjetada?.competencias?.length ?? 0;
  const faturamentoPeriodo = Boolean(faturamento?.periodo || periodLabel);
  const receitaPeriodo = Boolean(receitaProjetada?.periodo || periodLabel);
  const cobranca = cobrancaPositionSublabel(dashboard, periodLabel);

  return [
    {
      id: 'exec-faturamento-mes',
      label: faturamentoPeriodo ? 'Faturamento (período)' : 'Faturamento (mês)',
      value: faturamento != null ? formatCurrency(Number(faturamento.totalPago ?? 0)) : '—',
      sublabel: faturamento
        ? faturamentoPeriodo && periodLabel
          ? `${periodLabel} · ERP FlyStart`
          : `${formatMonthYear(faturamento.mes, faturamento.ano)} · ERP FlyStart`
        : 'ERP FlyStart',
      valueTone: faturamento && faturamento.totalPago > 0 ? 'good' : 'neutral',
      source: 'erp',
      ...buildTrend(faturamento?.variacaoPct, compareLabel),
    },
    {
      id: 'exec-valor-carteira',
      label: 'Valor em carteira',
      value: dashboard != null ? formatCurrency(valorCarteira) : '—',
      sublabel: cobranca.sublabel,
      valueTone: valorCarteira > 0 ? 'bad' : 'neutral',
      source: 'api',
      isSnapshot: cobranca.isSnapshot,
      positionBadge: cobranca.badgeLabel,
    },
    {
      id: 'exec-titulos-vencidos',
      label: 'Títulos vencidos',
      value: dashboard != null ? String(titulosVencidos) : '—',
      sublabel: cobranca.sublabel,
      valueTone: titulosVencidos === 0 ? 'good' : 'bad',
      source: 'api',
      isSnapshot: cobranca.isSnapshot,
      positionBadge: cobranca.badgeLabel,
    },
    {
      id: 'exec-clientes-inad',
      label: 'Clientes inadimplentes',
      value: dashboard != null ? String(clientesInad) : '—',
      sublabel: cobranca.sublabel,
      valueTone: clientesInad === 0 ? 'good' : 'bad',
      source: 'api',
      isSnapshot: cobranca.isSnapshot,
      positionBadge: cobranca.badgeLabel,
    },
    {
      id: 'exec-aging-leve',
      label: 'Vencidos 1–2 dias',
      value: dashboard != null ? String(agingLeve) : '—',
      sublabel: cobranca.sublabel,
      valueTone: agingLeve === 0 ? 'good' : 'bad',
      source: 'api',
      isSnapshot: cobranca.isSnapshot,
      positionBadge: cobranca.badgeLabel,
    },
    {
      id: 'exec-receita-projetada',
      label: receitaPeriodo ? 'Receita projetada (período)' : 'Receita projetada',
      value: receitaProjetada != null ? formatCurrency(Number(receitaProjetada.totalAReceber ?? 0)) : '—',
      sublabel:
        receitaProjetada != null
          ? receitaPeriodo && periodLabel
            ? `${periodLabel} · ERP FlyStart`
            : competencias > 0
              ? `${competencias} competência${competencias === 1 ? '' : 's'} · ERP FlyStart`
              : 'Hoje em diante · ERP FlyStart'
          : 'ERP FlyStart',
      valueTone: 'neutral',
      source: 'erp',
      ...buildTrend(receitaProjetada?.variacaoPct, compareLabel),
    },
  ];
}

export function dashboardToAgingChart(stats: DiretoriaDashboardStats | null | undefined) {
  if (!stats) return null;
  return [
    {
      name: '1–2 dias',
      valor: Number(stats.aging1a2Dias?.valor ?? 0),
      valor2: Number(stats.aging1a2Dias?.count ?? 0),
    },
    {
      name: '3–6 dias',
      valor: Number(stats.aging3a6Dias?.valor ?? 0),
      valor2: Number(stats.aging3a6Dias?.count ?? 0),
    },
    {
      name: '7+ dias',
      valor: Number(stats.agingAcima7Dias?.valor ?? 0),
      valor2: Number(stats.agingAcima7Dias?.count ?? 0),
    },
  ];
}

export function dashboardToEmpresaBar(stats: DiretoriaDashboardStats | null | undefined, take = 8) {
  const rows = stats?.titulosPorEmpresa;
  if (!Array.isArray(rows) || rows.length === 0) return [];
  return [...rows]
    .sort((a, b) => Number(b.valor ?? 0) - Number(a.valor ?? 0))
    .slice(0, take)
    .map((r) => ({
      name: r.empresa?.length > 18 ? `${r.empresa.slice(0, 16)}…` : r.empresa,
      valor: Number(r.count ?? 0),
      valor2: Number(r.valor ?? 0),
    }));
}

export const DIRETORIA_SNAPSHOT_INDICATOR_IDS = new Set([
  'motos-disponiveis',
  'motos-status',
  'custo-moto',
]);
